import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";

/**
 * ResetPassword — consumes a one-time token from the URL and lets
 * the user set a new password. Backend enforces min 8 chars; we
 * mirror that client-side plus "both fields match".
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = useMemo(() => params.get("token") || "", [params]);

    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw1.length < 8) {
            setErr("La password deve avere almeno 8 caratteri.");
            return;
        }
        if (pw1 !== pw2) {
            setErr("Le due password non coincidono.");
            return;
        }
        if (!token) {
            setErr("Link non valido o scaduto. Richiedi un nuovo link dal login.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw1 });
            setDone(true);
            // Force a fresh login — clear any stale session artefact.
            try { localStorage.removeItem("echoes_jwt"); } catch { /* noop */ }
            setTimeout(() => nav("/login"), 2500);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "invalid_or_expired") {
                setErr("Il link è scaduto o è già stato usato. Richiedine uno nuovo dal login.");
            } else {
                setErr("Qualcosa non ha funzionato. Riprova fra un momento.");
            }
        } finally {
            setBusy(false);
        }
    };

    // Missing token — show a guidance screen instead of an empty form.
    if (!token) {
        return (
            <div className="grain min-h-screen candle-halo">
                <Header minimal />
                <div className="max-w-md mx-auto px-6 pt-20 text-center">
                    <h1 className="h-serif text-3xl md:text-4xl mb-5">Link incompleto.</h1>
                    <p className="text-ink-textMid mb-8 leading-relaxed">
                        Per reimpostare la password devi aprire il link che hai ricevuto via email.
                        Se non lo trovi, puoi richiederne uno nuovo.
                    </p>
                    <Link to="/auth/forgot-password" className="btn-primary inline-block" data-testid="reset-request-new">
                        Richiedi nuovo link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="grain min-h-screen candle-halo" data-testid="reset-password">
            <Header minimal />
            <div className="max-w-md mx-auto px-6 pt-16 pb-24 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                    <BrandMark size={26} />
                    <p className="label-eyebrow">Nuova password</p>
                </div>

                {!done ? (
                    <>
                        <h1 className="h-serif text-4xl md:text-5xl mb-6 leading-tight">
                            Scegli una <span className="italic text-ink-gold">nuova.</span>
                        </h1>
                        <p className="text-ink-textMid leading-relaxed mb-10">
                            Almeno 8 caratteri. Meglio se non la usi da nessun'altra parte.
                        </p>

                        <form onSubmit={submit} className="space-y-7">
                            <div>
                                <label className="label-eyebrow" htmlFor="pw1">Nuova password</label>
                                <input
                                    id="pw1"
                                    type="password"
                                    data-testid="reset-pw1"
                                    className="input-line mt-2"
                                    value={pw1}
                                    onChange={(e) => setPw1(e.target.value)}
                                    minLength={8}
                                    required
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="label-eyebrow" htmlFor="pw2">Ripeti la password</label>
                                <input
                                    id="pw2"
                                    type="password"
                                    data-testid="reset-pw2"
                                    className="input-line mt-2"
                                    value={pw2}
                                    onChange={(e) => setPw2(e.target.value)}
                                    minLength={8}
                                    required
                                />
                            </div>

                            {err && (
                                <p data-testid="reset-error" className="text-ink-danger text-sm font-serif italic">
                                    {err}
                                </p>
                            )}

                            <button
                                disabled={busy}
                                data-testid="reset-submit"
                                className="btn-primary w-full"
                            >
                                {busy ? "…" : "Conferma nuova password"}
                            </button>
                        </form>
                    </>
                ) : (
                    <div data-testid="reset-done" className="text-center">
                        <h1 className="h-serif text-4xl md:text-5xl mb-6 leading-tight">
                            Fatto.
                        </h1>
                        <p className="text-ink-textMid leading-relaxed mb-4">
                            La tua password è stata aggiornata. Ti stiamo portando al login…
                        </p>
                        <p className="text-sm text-ink-textLow font-serif italic">
                            La tua presenza ti aspetta.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
